import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API, WS_URL } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatCircle, Users, User, WifiHigh, WifiSlash, MagnifyingGlass } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";

export default function ChatPage() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [chatType, setChatType] = useState("chapter");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [lastMessages, setLastMessages] = useState({});
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const dmInitialized = useRef(false);
  const reconnectTimer = useRef(null);
  const pollTimer = useRef(null);
  const currentRoomRef = useRef(null);

  useEffect(() => {
    fetchMembers();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  // Poll online users every 10 seconds
  useEffect(() => {
    const fetchOnline = async () => {
      try {
        const res = await axios.get(`${API}/chat/online`, { headers: { Authorization: `Bearer ${token}` } });
        setOnlineUsers(res.data);
      } catch {}
    };
    fetchOnline();
    const interval = setInterval(fetchOnline, 10000);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch last messages for all members to sort sidebar
  useEffect(() => {
    if (members.length === 0) return;
    const fetchLastMsgs = async () => {
      try {
        const res = await axios.get(`${API}/chat/last-messages`, { headers: { Authorization: `Bearer ${token}` } });
        const map = {};
        (res.data || []).forEach(m => { map[m.other_user_id] = m; });
        setLastMessages(map);
      } catch {}
    };
    fetchLastMsgs();
    const interval = setInterval(fetchLastMsgs, 15000);
    return () => clearInterval(interval);
  }, [members, token]);

  // WebSocket connection with auto-reconnect + message polling fallback
  useEffect(() => {
    let roomId;
    if (chatType === "chapter") {
      roomId = user?.chapter_id;
    } else if (selectedRecipient) {
      const ids = [user.id, selectedRecipient.id].sort();
      roomId = `dm_${ids[0]}_${ids[1]}`;
    }
    
    if (!roomId) return;
    currentRoomRef.current = roomId;

    // Close existing connection
    if (wsRef.current) { wsRef.current.close(); }
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); }
    if (pollTimer.current) { clearInterval(pollTimer.current); }

    // Fetch existing messages
    const fetchId = chatType === "chapter" ? roomId : selectedRecipient?.id;
    if (fetchId) fetchMessages(fetchId);

    // Poll messages every 5s as fallback for real-time delivery
    pollTimer.current = setInterval(() => {
      if (fetchId) fetchMessages(fetchId);
    }, 5000);

    const connectWs = () => {
      const wsUrl = `${WS_URL}/api/ws/chat/${roomId}?user_id=${user?.id}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setMessages((prev) => {
            if (prev.some(m => m.id === message.id)) return prev;
            return [...prev, message];
          });
        } catch (e) {
          console.error("Failed to parse message:", e);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        // Auto-reconnect after 3s if still on same room
        if (currentRoomRef.current === roomId) {
          reconnectTimer.current = setTimeout(connectWs, 3000);
        }
      };

      ws.onerror = () => {
        setWsConnected(false);
      };

      wsRef.current = ws;
    };

    connectWs();

    return () => {
      currentRoomRef.current = null;
      if (wsRef.current) { wsRef.current.close(); }
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); }
      if (pollTimer.current) { clearInterval(pollTimer.current); }
    };
  }, [chatType, selectedRecipient, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.filter(m => m.id !== user?.id));
    } catch (error) {
      console.error("Failed to fetch members:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle DM redirect from members page & auto-select DM for users without chapter
  useEffect(() => {
    if (dmInitialized.current || members.length === 0) return;
    const dmId = searchParams.get("dm");
    const dmName = searchParams.get("name");
    if (dmId) {
      const member = members.find(m => m.id === dmId);
      if (member) {
        handleSelectChat("direct", member);
      } else if (dmName) {
        handleSelectChat("direct", { id: dmId, full_name: decodeURIComponent(dmName) });
      }
      dmInitialized.current = true;
    } else if (!user?.chapter_id && members.length > 0) {
      // Auto-select first DM for users without a chapter (e.g. super_admin)
      handleSelectChat("direct", members[0]);
      dmInitialized.current = true;
    }
  }, [members, searchParams]);

  const fetchMessages = async (recipientId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${chatType}/${recipientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const sendMessage = useCallback(() => {
    if (!newMessage.trim()) return;

    const messageData = {
      sender_id: user?.id,
      sender_name: user?.full_name,
      content: newMessage,
      chat_type: chatType
    };

    // Try WebSocket first
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(messageData));
      setNewMessage("");
    } else {
      // Fallback to HTTP
      sendMessageHTTP();
    }
  }, [newMessage, user, chatType]);

  const sendMessageHTTP = async () => {
    try {
      await axios.post(`${API}/chat/messages`, {
        content: newMessage,
        chat_type: chatType,
        recipient_id: chatType === "chapter" ? user?.chapter_id : selectedRecipient?.id
      }, { headers: { Authorization: `Bearer ${token}` } });

      setNewMessage("");
      
      // Refresh messages
      const roomId = chatType === "chapter" ? user?.chapter_id : selectedRecipient?.id;
      if (roomId) {
        fetchMessages(roomId);
      }
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSelectChat = (type, recipient = null) => {
    setChatType(type);
    setSelectedRecipient(recipient);
    setMessages([]);
  };

  return (
    <>
      <SEO title="Chat" description="Connect with your chapter members through real-time messaging." path="/chat" noIndex />
      <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)] flex flex-col md:flex-row gap-4 md:gap-6 animate-fadeInUp">
        {/* Sidebar - Chat List */}
        <div className="w-full md:w-80 bg-card border border-[var(--border-color)] rounded-2xl flex flex-col h-48 md:h-full">
          <div className="p-3 md:p-4 border-b border-[var(--border-color)] flex items-center justify-between">
            <h2 className="text-primary font-semibold text-base md:text-lg">Messages</h2>
          </div>

          {/* Chapter Chat */}
          <button
            onClick={() => handleSelectChat("chapter")}
            className={`flex items-center gap-3 p-3 md:p-4 border-b border-[var(--border-color)] transition-colors ${
              chatType === "chapter" ? "bg-bch-teal/10" : "hover:bg-[var(--bg-surface)]"
            }`}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-bch-teal/20 flex items-center justify-center">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-bch-teal" weight="duotone" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-primary font-medium text-sm md:text-base truncate">{user?.chapter_name || "Chapter"}</p>
              <p className="text-muted text-xs md:text-sm">Group Chat</p>
            </div>
          </button>

          {/* Direct Messages - Hidden on mobile, visible on desktop */}
          <div className="hidden md:block px-3 pt-3 pb-2 border-b border-[var(--border-color)] space-y-2">
            <p className="text-muted text-xs uppercase tracking-wider">Direct Messages</p>
            <div className="relative">
              <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" weight="bold" />
              <Input
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search members..."
                data-testid="chat-member-search"
                className="pl-8 h-8 text-xs bg-surface border-[var(--border-color)] text-primary placeholder:text-muted"
              />
            </div>
          </div>

          <ScrollArea className="flex-1 hidden md:block">
            {members
              .filter(m => !memberSearch || m.full_name.toLowerCase().includes(memberSearch.toLowerCase()) || (m.business_category || "").toLowerCase().includes(memberSearch.toLowerCase()))
              .sort((a, b) => {
                const aTime = lastMessages[a.id]?.created_at || "";
                const bTime = lastMessages[b.id]?.created_at || "";
                return bTime.localeCompare(aTime);
              })
              .map((member) => (
              <button
                key={member.id}
                onClick={() => handleSelectChat("direct", member)}
                className={`w-full flex items-center gap-3 p-4 border-b border-[var(--border-color)] transition-colors ${
                  selectedRecipient?.id === member.id ? "bg-bch-gold/10" : "hover:bg-[var(--bg-surface)]"
                }`}
                data-testid={`chat-member-${member.id}`}
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-bch-gold/20 flex items-center justify-center">
                    <span className="text-bch-gold font-bold">{member.full_name.charAt(0)}</span>
                  </div>
                  <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[var(--bg-card)] ${onlineUsers.includes(member.id) ? "bg-green-500" : "bg-gray-400"}`} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-primary font-medium truncate">{member.full_name}</p>
                  <p className="text-muted text-xs truncate">
                    {lastMessages[member.id]?.last_content
                      ? (lastMessages[member.id].last_content.length > 30 
                          ? lastMessages[member.id].last_content.slice(0, 30) + "..." 
                          : lastMessages[member.id].last_content)
                      : member.business_category}
                  </p>
                </div>
              </button>
            ))}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-card border border-[var(--border-color)] rounded-2xl flex flex-col min-h-0">
          {/* Chat Header */}
          <div className="p-3 md:p-4 border-b border-[var(--border-color)] flex items-center gap-3">
            {chatType === "chapter" ? (
              <>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-bch-teal/20 flex items-center justify-center">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-bch-teal" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-primary font-semibold text-sm md:text-base truncate">{user?.chapter_name}</p>
                  <p className="text-muted text-xs md:text-sm">Chapter Group Chat</p>
                </div>
              </>
            ) : selectedRecipient ? (
              <>
                <div className="relative">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-bch-gold/20 flex items-center justify-center">
                    <span className="text-bch-gold font-bold text-sm md:text-base">{selectedRecipient.full_name.charAt(0)}</span>
                  </div>
                  <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-[var(--bg-card)] ${onlineUsers.includes(selectedRecipient.id) ? "bg-green-500" : "bg-gray-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-primary font-semibold text-sm md:text-base truncate">{selectedRecipient.full_name}</p>
                  <p className={`text-xs md:text-sm ${onlineUsers.includes(selectedRecipient.id) ? "text-green-500 font-medium" : "text-muted"}`}>
                    {onlineUsers.includes(selectedRecipient.id) ? "Online" : "Offline"}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-muted">
                <ChatCircle className="w-5 h-5" />
                <span className="text-sm">Select a conversation</span>
              </div>
            )}
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-3 md:p-4 chat-container">
            <div className="space-y-3 md:space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12 md:py-20">
                  <ChatCircle className="w-12 h-12 md:w-16 md:h-16 text-muted mx-auto mb-4" weight="duotone" />
                  <p className="text-muted">No messages yet</p>
                  <p className="text-muted text-xs md:text-sm">Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMine = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-[85%] md:max-w-[70%] ${isMine ? "order-2" : ""}`}>
                        {!isMine && chatType === "chapter" && (
                          <p className="text-muted text-xs mb-1 ml-1">{msg.sender_name}</p>
                        )}
                        <div className={`px-3 md:px-4 py-2 md:py-3 ${
                          isMine
                            ? "chat-bubble-sent text-white"
                            : "chat-bubble-received text-primary"
                        }`}>
                          <p className="text-sm">{msg.content}</p>
                        </div>
                        <p className={`text-muted text-xs mt-1 ${isMine ? "text-right mr-1" : "ml-1"}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message Input */}
          {(chatType === "chapter" || selectedRecipient) && (
            <div className="p-3 md:p-4 border-t border-[var(--border-color)]">
              <div className="flex gap-2 md:gap-3">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  data-testid="chat-input"
                  className="flex-1 bg-surface border-[var(--border-color)] text-primary placeholder:text-muted text-sm md:text-base"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-bch-teal hover:bg-bch-teal-dark px-3 md:px-4"
                  data-testid="send-message-btn"
                >
                  <PaperPlaneTilt className="w-5 h-5" weight="fill" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
